import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ArrowRight, User, Mail, Clock } from 'lucide-react';

export default function Register() {
  const [type, setType] = useState('vendedor');
  const [constants, setConstants] = useState({ supervisors: [] });
  const [loading, setLoading] = useState(false);
  const { registerSupervisor } = useAuth();
  const navigate = useNavigate();

  // Vendor fields
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [fichaCasmu, setFichaCasmu] = useState('');
  const [horaEntrada, setHoraEntrada] = useState('');
  const [horaDescansoSalida, setHoraDescansoSalida] = useState('');
  const [horaDescansoVuelta, setHoraDescansoVuelta] = useState('');
  const [horaSalida, setHoraSalida] = useState('');

  // Supervisor fields
  const [supervisorName, setSupervisorName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    api.getConstants().then(setConstants).catch(() => {});
  }, []);

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!nombre || !apellido || !fichaCasmu || !horaEntrada || !horaSalida) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      await api.registerVendor({
        nombre,
        apellido,
        ficha_casmu: fichaCasmu,
        hora_entrada: horaEntrada,
        hora_descanso_salida: horaDescansoSalida,
        hora_descanso_vuelta: horaDescansoVuelta,
        hora_salida: horaSalida
      });
      toast.success('Solicitud enviada. Se enviara un email para aprobar tu registro.');
      navigate('/login');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  const handleSupervisorSubmit = async (e) => {
    e.preventDefault();
    if (!supervisorName || !email) {
      toast.error('Completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      await registerSupervisor({ nombre: supervisorName, email });
      toast.success('Solicitud enviada. Espera la aprobacion del auditor.');
      navigate('/login');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - BMW M Series Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#00205B]">
        <div className="absolute inset-0 flex flex-col items-center justify-center px-12">
          <div className="w-24 h-24 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center mb-8">
            <span className="text-4xl font-black text-white tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>DC</span>
          </div>
          <h2 className="text-3xl font-bold text-white tracking-tight text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Departamento Comercial</h2>
          <p className="text-white/60 text-sm mt-3 text-center">Sistema de Gestion de Ventas</p>
          <div className="flex gap-2 mt-8">
            <div className="w-16 h-1 rounded-full bg-[#0066B1]" />
            <div className="w-16 h-1 rounded-full bg-[#E3001B]" />
            <div className="w-16 h-1 rounded-full bg-[#00205B]" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 m-stripe" />
      </div>

      {/* Right side - Register Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-4 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-md bg-[#00205B] flex items-center justify-center shadow-lg mb-3">
              <span className="text-white text-2xl font-bold">DC</span>
            </div>
            <h1 className="text-lg font-bold text-[#0A0A0A]">Crear Cuenta</h1>
          </div>

          <div className="segmented-control w-full mb-6">
            <button type="button" onClick={() => setType('vendedor')} className={`segmented-button flex-1 ${type === 'vendedor' ? 'active' : ''}`} data-testid="register-type-vendedor">
              Vendedor
            </button>
            <button type="button" onClick={() => setType('supervisor')} className={`segmented-button flex-1 ${type === 'supervisor' ? 'active' : ''}`} data-testid="register-type-supervisor">
              Supervisor
            </button>
          </div>

          {type === 'vendedor' ? (
            <form onSubmit={handleVendorSubmit} className="space-y-3">
              <div>
                <label className="form-label">Ficha CASMU (sera tu usuario)</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={fichaCasmu} onChange={(e) => setFichaCasmu(e.target.value)} className="form-input pl-9" placeholder="Ej: 12345" data-testid="register-ficha" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Nombre</label>
                  <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} className="form-input" placeholder="Nombre" data-testid="register-nombre" />
                </div>
                <div>
                  <label className="form-label">Apellido</label>
                  <input type="text" value={apellido} onChange={(e) => setApellido(e.target.value)} className="form-input" placeholder="Apellido" data-testid="register-apellido" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Hora Entrada *</label>
                  <div className="relative">
                    <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="time" value={horaEntrada} onChange={(e) => setHoraEntrada(e.target.value)} className="form-input pl-9" data-testid="register-hora-entrada" />
                  </div>
                </div>
                <div>
                  <label className="form-label">Hora Salida *</label>
                  <div className="relative">
                    <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="time" value={horaSalida} onChange={(e) => setHoraSalida(e.target.value)} className="form-input pl-9" data-testid="register-hora-salida" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Sale descanso</label>
                  <input type="time" value={horaDescansoSalida} onChange={(e) => setHoraDescansoSalida(e.target.value)} className="form-input" data-testid="register-descanso-salida" />
                </div>
                <div>
                  <label className="form-label">Vuelve descanso</label>
                  <input type="time" value={horaDescansoVuelta} onChange={(e) => setHoraDescansoVuelta(e.target.value)} className="form-input" data-testid="register-descanso-vuelta" />
                </div>
              </div>

              <div className="bg-[#0082D2]/10 border border-[#0082D2]/20 rounded-md p-3 text-[12px] text-[#00205B]">
                Tu solicitud sera enviada para aprobacion. Una vez aprobada, tu password sera <strong>casmu2026</strong>
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="register-submit">
                {loading ? 'Enviando...' : <><span>Enviar Solicitud</span><ArrowRight size={16} /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSupervisorSubmit} className="space-y-3">
              <div>
                <label className="form-label">Nombre Completo</label>
                <input type="text" value={supervisorName} onChange={(e) => setSupervisorName(e.target.value)} className="form-input" placeholder="Tu nombre y apellido" data-testid="register-supervisor-name" />
              </div>

              <div>
                <label className="form-label">Email @casmu.com</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="form-input pl-9" placeholder="tu.email@casmu.com" data-testid="register-email" />
                </div>
              </div>

              <div className="bg-[#0082D2]/10 border border-[#0082D2]/20 rounded-md p-3 text-[12px] text-[#00205B]">
                Los supervisores requieren aprobacion del auditor antes de poder acceder al sistema.
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="register-submit">
                {loading ? 'Enviando...' : <><span>Enviar Solicitud</span><ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          <div className="mt-5 text-center">
            <p className="text-[13px] text-gray-500">
              Ya tenes cuenta?{' '}
              <Link to="/login" className="text-[#0082D2] font-semibold hover:underline" data-testid="login-link">
                Inicia sesion
              </Link>
            </p>
          </div>

          <div className="mt-4 py-3 border-t border-gray-200 text-center">
            <span className="text-[10px] text-gray-400 tracking-widest font-medium">MEBS</span>
          </div>
        </div>
      </div>
    </div>
  );
}
